<script setup>
import {computed, inject, reactive} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfigData} from "@/composables/useConfigData.js";
import {useVuelidate} from "@vuelidate/core";

//stores
const userStore = useUserStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();
const {games} = useConfigData('Currency')

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  user: {
    type: Object,
    required: true
  }
});

//injects
const visible = inject('isShowDiscordInfoModal')

//component variables
const milliseconds = 1776069191919
const timestamp = Date.now();

const discordInfo = reactive({
  "userID": props.user.id ?? null,
  "discordID": props.user.discordID ?? Math.trunc((timestamp - milliseconds) / 30000),
  "discordUsername": props.user.discordUsername ?? null,
  "discordDisplayName": props.user.discordDisplayName ?? null,
  "discordAvatar": props.user.discordAvatar ?? null
})
const rules = computed(() => {
  return {
    userID: {required},
    discordID: {required},
    discordUsername: {required},
  };
});
const $vuelidate = useVuelidate(rules, discordInfo);

//global functions
//component functions
const onSave = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }
  try {
    saving.value = true
    const res = await userStore.action_setAuthenticationDiscord(discordInfo)
    if (res.result === true) {
      showMessage('Discord info edited.', 'success');
      emit('refresh')
      setTimeout(() => {
        visible.value = false
      }, 200)
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Edit Discord Info
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-96 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="lowestPricesLink" v-model="discordInfo.discordUsername" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.discordUsername.$error }"/>
            <label for="lowestPricesLink" class="!bg-slate-900"
                   :class="{ 'focus:text-red-300': $vuelidate.discordUsername.$error }">Discord Username</label>
          </FloatLabel>
          <template v-if="$vuelidate.discordUsername.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.discordUsername.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="lowestPricesLink" v-model="discordInfo.discordDisplayName" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="lowestPricesLink" class="!bg-slate-900">Discord Display Name</label>
          </FloatLabel>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="lowestPricesLink" v-model="discordInfo.discordAvatar" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="lowestPricesLink" class="!bg-slate-900">Discord Avatar</label>
          </FloatLabel>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style></style>